import { apiFetch } from '../utils/apiFetch';
import type { ApiEndpoints } from '../types/ApiEndpoints';

export interface AssistantConversation {
  thread_id: string;
  persona: string;
}

export interface AssistantMessage {
  role: string;
  content: string | null;
  created_at?: string;
}

/** A parsed SSE frame from the streaming chat endpoint. */
export type AssistantStreamFrame =
  | { type: 'start' }
  | { type: 'token'; text: string }
  | { type: 'tool'; name: string }
  | { type: 'done' }
  | { type: 'error'; message: string };

export interface StreamChatOptions {
  threadId: string;
  message: string;
  balloonId?: string | null;
}

/**
 * Client for the read-only Beat Engineer assistant. Create a conversation, then
 * stream the answer over Server-Sent Events. Advanced/event-scoped behaviour is
 * gated server-side by the caller's permission, so this client is deliberately thin.
 */
export class AssistantService {
  constructor(
    private endpoints: Pick<ApiEndpoints, 'assistantConversations' | 'assistantChat'>,
    private apiKey?: string,
    private partnerKey?: string | null
  ) {}

  /** Start a conversation, optionally scoped to a specific event (balloon). */
  async createConversation(token?: string, balloonId?: string | null): Promise<AssistantConversation> {
    const res = await apiFetch(this.endpoints.assistantConversations, {
      method: 'POST',
      apiKey: this.apiKey,
      partnerKey: this.partnerKey,
      authToken: token,
      body: JSON.stringify(balloonId ? { balloon_id: balloonId } : {}),
    });
    if (!res.ok) {
      throw new Error('Failed to start a conversation');
    }
    return res.json();
  }

  /** Load the persisted history of a conversation. */
  async getMessages(threadId: string, token?: string): Promise<AssistantMessage[]> {
    const res = await apiFetch(
      `${this.endpoints.assistantConversations}/${encodeURIComponent(threadId)}/messages`,
      {
        method: 'GET',
        apiKey: this.apiKey,
        partnerKey: this.partnerKey,
        authToken: token,
      }
    );
    if (!res.ok) {
      throw new Error('Failed to load messages');
    }
    const data = await res.json();
    return data.messages ?? [];
  }

  /**
   * Send a message and stream the reply. `onFrame` is called for every SSE frame
   * (tokens, tool notices, done, error). Resolves when the stream closes.
   */
  async streamChat(
    { threadId, message, balloonId }: StreamChatOptions,
    token: string | undefined,
    onFrame: (frame: AssistantStreamFrame) => void
  ): Promise<void> {
    const res = await apiFetch(this.endpoints.assistantChat, {
      method: 'POST',
      apiKey: this.apiKey,
      partnerKey: this.partnerKey,
      authToken: token,
      body: JSON.stringify({
        thread_id: threadId,
        message,
        ...(balloonId ? { balloon_id: balloonId } : {}),
      }),
    });

    if (!res.ok || !res.body) {
      onFrame({ type: 'error', message: 'The assistant is unavailable right now.' });
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      let separatorIndex: number;
      while ((separatorIndex = buffer.indexOf('\n\n')) !== -1) {
        const raw = buffer.slice(0, separatorIndex).trim();
        buffer = buffer.slice(separatorIndex + 2);

        if (!raw.startsWith('data:')) continue;

        try {
          onFrame(JSON.parse(raw.slice(5).trim()) as AssistantStreamFrame);
        } catch {
          // ignore malformed frames
        }
      }
    }
  }
}
